// Capoeira songbook: lyrics, translations, recordings and sources, sourced
// verbatim from songs.json (do not reword or reorder the lyrics themselves).
import songsData from '../../data/songs.json'

export type SongLyricBlock = { pt: string; en: string }
export type SongRecording = { label: string; url: string }
export type SongSource = { label: string; url: string }
export type Song = {
  title: string
  description: string
  // Empty when the lyrics haven't been transcribed yet; the card shows a
  // "Lyrics coming soon" note in that case instead of the PT/EN columns.
  lyrics: SongLyricBlock[]
  // Empty when no recording has been found yet; the card shows a
  // "Recording coming soon" note in that case instead of any pills.
  recordings: SongRecording[]
  // Omitted for songs with no lyrics yet, since there's nothing to attribute.
  source?: SongSource
}

// Portuguese collation so accented titles sort by their base letter
// ("É amor demais" under E, not after Z).
const titleCollator = new Intl.Collator('pt', { sensitivity: 'base' })

export const SONGS: Song[] = (songsData as Song[]).slice().sort((a, b) => titleCollator.compare(a.title, b.title))

function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase('pt')
}

// Case- and diacritic-insensitive match against the title or any Portuguese
// lyric line — an empty query matches everything.
export function matchesSongQuery(song: Song, query: string): boolean {
  const q = normalize(query.trim())
  if (!q) return true
  if (normalize(song.title).includes(q)) return true
  return song.lyrics.some((block) => normalize(block.pt).includes(q))
}

// A recording link's YouTube embed URL, for playing it inline instead of
// opening a new tab — handles watch?v=, youtu.be/, and already-embed URLs.
// Returns null for non-YouTube recordings (e.g. Bandcamp), which just keep
// opening in a new tab. `jsApi` turns on YouTube's postMessage control channel
// (play/pause from outside the iframe); `origin` is required by that channel.
export type EmbedOptions = { autoplay?: boolean; jsApi?: boolean; origin?: string }

export function youTubeEmbedUrl(url: string, { autoplay = true, jsApi = false, origin }: EmbedOptions = {}): string | null {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null
  }
  const host = parsed.hostname.replace(/^www\.|^m\./, '')
  let id: string | null = null
  if (host === 'youtu.be') {
    id = parsed.pathname.slice(1) || null
  } else if (host === 'youtube.com') {
    if (parsed.pathname === '/watch') id = parsed.searchParams.get('v')
    else {
      const embedMatch = parsed.pathname.match(/^\/embed\/([^/]+)/)
      if (embedMatch) id = embedMatch[1]
    }
  }
  if (!id) return null
  let query = `autoplay=${autoplay ? 1 : 0}`
  if (jsApi) query += '&enablejsapi=1&playsinline=1'
  if (jsApi && origin) query += `&origin=${encodeURIComponent(origin)}`
  return `https://www.youtube-nocookie.com/embed/${id}?${query}`
}

// The recording the shared player should load for a song — its first
// YouTube recording, or undefined when there's nothing embeddable.
export function firstEmbeddableRecording(song: Song): SongRecording | undefined {
  return song.recordings.find((recording) => youTubeEmbedUrl(recording.url) !== null)
}
